var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PolarArea } from "react-chartjs-2";
import { ArcElement, Chart as ChartJS, RadialLinearScale } from "chart.js";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import "./HeidrickScoreChart.css";
import CardTooltip from "../CardTooltip";
import { Icon } from "@eightfold.ai/octuple";
import { IconPathName } from "../../IconPath/mdi";
ChartJS.register(RadialLinearScale, ArcElement);
var ScoreColors;
(function (ScoreColors) {
    ScoreColors["Level3"] = "var(--good-1)";
    ScoreColors["Level2"] = "var(--neutral-1)";
    ScoreColors["Level1"] = "var(--bad-1)";
})(ScoreColors || (ScoreColors = {}));
var BorderColors;
(function (BorderColors) {
    BorderColors["Level3"] = "var(--good-2)";
    BorderColors["Level2"] = "var(--neutral-2)";
    BorderColors["Level1"] = "var(--bad-2)";
})(BorderColors || (BorderColors = {}));
var ScoreText;
(function (ScoreText) {
    ScoreText["Level3"] = "Maintain";
    ScoreText["Level2"] = "Watch";
    ScoreText["Level1"] = "Act";
})(ScoreText || (ScoreText = {}));
var decideBorderColor = function (score) {
    switch (true) {
        case score < 80:
            return BorderColors.Level1;
        case score < 90:
            return BorderColors.Level2;
        case score >= 90:
            return BorderColors.Level3;
        default:
            return BorderColors.Level1;
    }
};
var options = {
    scales: {
        r: {
            max: 100,
            min: -70,
            ticks: {
                display: false,
                stepSize: 100,
            },
            grid: {
                color: "var(--grey-color-300)",
            },
            pointLabels: {
                display: false,
                centerPointLabels: true,
            },
        },
    },
};
var HeidrickScoreChart = function (_a) {
    var dataset = _a.dataset, _b = _a.overallScore, overallScore = _b === void 0 ? { score: 0 } : _b;
    var scoreValidation = function (score) {
        if (score < 50) {
            return 50;
        }
        return score;
    };
    var decideColor = function (score) {
        switch (true) {
            case score < 80:
                return ScoreColors.Level1;
            case score < 90:
                return ScoreColors.Level2;
            case score >= 90:
                return ScoreColors.Level3;
            default:
                return ScoreColors.Level1;
        }
    };
    var decideText = function (score) {
        switch (true) {
            case score < 80:
                return ScoreText.Level1;
            case score < 90:
                return ScoreText.Level2;
            case score >= 90:
                return ScoreText.Level3;
            default:
                return ScoreText.Level1;
        }
    };
    var scoreData = {
        datasets: [
            {
                data: [
                    scoreValidation(dataset[1].score),
                    scoreValidation(dataset[3].score),
                    scoreValidation(dataset[2].score),
                    scoreValidation(dataset[0].score),
                ],
                backgroundColor: [
                    decideColor(dataset[1].score),
                    decideColor(dataset[3].score),
                    decideColor(dataset[2].score),
                    decideColor(dataset[0].score),
                ],
                borderWidth: 2,
                hoverBorderWidth: 2,
                borderRadius: 15,
                borderColor: "var(--grey-color-300)",
            },
        ],
    };
    return (_jsxs("div", __assign({ className: "chart-container" }, { children: [_jsxs("div", __assign({ className: "left-info" }, { children: [_jsxs("div", __assign({ className: "l-top-area" }, { children: [_jsxs("div", __assign({ className: "area-text" }, { children: [_jsx("div", { className: "action-status", style: {
                                            backgroundColor: decideColor(dataset[0].score),
                                            border: "1px solid ".concat(decideBorderColor(dataset[0].score)),
                                        } }), _jsx("div", __assign({ style: {
                                            fontFamily: "var(--font-stack-full-medium)",
                                        } }, { children: dataset[0].label })), dataset[0].tooltip && (_jsx(CardTooltip, { tooltip: dataset[0].tooltip, questionIcon: _jsx(Icon, { path: IconPathName.mdiFilledQuestion, style: { color: "var(--grey-color-70)" } }) }))] })), _jsxs("div", __assign({ className: "area-percentage" }, { children: [_jsx("div", { children: "".concat(Math.round(dataset[0].score), "%") }), dataset[0].onClick && (_jsx(ArrowForwardOutlinedIcon, { className: "action-arrow", onClick: dataset[0].onClick }))] })), _jsx("div", __assign({ className: "pill-score", style: {
                                    backgroundColor: decideColor(dataset[0].score),
                                    fontFamily: "var(--font-stack-full-medium)",
                                } }, { children: decideText(dataset[0].score) }))] })), _jsxs("div", __assign({ className: "l-bot-area" }, { children: [_jsxs("div", __assign({ className: "area-text" }, { children: [_jsx("div", { className: "action-status", style: {
                                            backgroundColor: decideColor(dataset[2].score),
                                            border: "1px solid ".concat(decideBorderColor(dataset[2].score)),
                                        } }), _jsx("div", __assign({ style: {
                                            fontFamily: "var(--font-stack-full-medium)",
                                        } }, { children: dataset[2].label })), dataset[2].tooltip && (_jsx(CardTooltip, { tooltip: dataset[2].tooltip, questionIcon: _jsx(Icon, { path: IconPathName.mdiFilledQuestion, style: { color: "var(--grey-color-70)" } }) }))] })), _jsxs("div", __assign({ className: "area-percentage" }, { children: [_jsx("div", { children: "".concat(Math.round(dataset[2].score), "%") }), dataset[2].onClick && (_jsx(ArrowForwardOutlinedIcon, { className: "action-arrow", onClick: dataset[2].onClick }))] })), _jsx("div", __assign({ className: "pill-score", style: {
                                    backgroundColor: decideColor(dataset[2].score),
                                    fontFamily: "var(--font-stack-full-medium)",
                                } }, { children: decideText(dataset[2].score) }))] }))] })), _jsxs("div", __assign({ className: "polar-wrapper" }, { children: [_jsx(PolarArea, { data: scoreData, options: options }), _jsx("div", __assign({ className: "center-circle" }, { children: _jsxs("div", __assign({ className: "center-content" }, { children: [_jsxs("div", __assign({ style: {
                                        fontFamily: "var(--font-stack-full-medium)",
                                    }, className: "text-center" }, { children: [_jsx("div", { children: "Overall \n" }), _jsxs("div", __assign({ style: {
                                                display: "flex",
                                                gap: "8px",
                                                alignItems: "center",
                                            } }, { children: ["Heidrick Score", (overallScore === null || overallScore === void 0 ? void 0 : overallScore.tooltip) && (_jsx("div", __assign({ className: "tooltipStyle" }, { children: _jsx(CardTooltip, { tooltip: overallScore.tooltip, questionIcon: _jsx(Icon, { path: IconPathName.mdiFilledQuestion, style: { color: "var(--grey-color-70)" } }) }) })))] }))] })), _jsxs("div", __assign({ className: "area-percentage" }, { children: [_jsx("div", { children: "".concat(Math.round(overallScore.score), "%") }), overallScore.onClick && (_jsx(ArrowForwardOutlinedIcon, { className: "action-arrow", onClick: overallScore.onClick }))] })), _jsx("div", __assign({ className: "pill-score", style: {
                                        backgroundColor: decideColor(overallScore.score),
                                        fontFamily: "var(--font-stack-full-medium)",
                                    } }, { children: decideText(overallScore.score) }))] })) }))] })), _jsxs("div", __assign({ className: "right-info" }, { children: [_jsxs("div", __assign({ className: "r-top-area" }, { children: [_jsxs("div", __assign({ className: "area-text" }, { children: [_jsx("div", { className: "action-status", style: {
                                            backgroundColor: decideColor(dataset[1].score),
                                            border: "1px solid ".concat(decideBorderColor(dataset[1].score)),
                                        } }), _jsx("div", __assign({ style: {
                                            fontFamily: "var(--font-stack-full-medium)",
                                        } }, { children: dataset[1].label })), dataset[1].tooltip && (_jsx(CardTooltip, { tooltip: dataset[1].tooltip, questionIcon: _jsx(Icon, { path: IconPathName.mdiFilledQuestion, style: { color: "var(--grey-color-70)" } }) }))] })), _jsxs("div", __assign({ className: "area-percentage" }, { children: [_jsx("div", { children: "".concat(Math.round(dataset[1].score), "%") }), dataset[1].onClick && (_jsx(ArrowForwardOutlinedIcon, { className: "action-arrow", onClick: dataset[1].onClick }))] })), _jsx("div", __assign({ className: "pill-score", style: {
                                    backgroundColor: decideColor(dataset[1].score),
                                    fontFamily: "var(--font-stack-full-medium)",
                                } }, { children: decideText(dataset[1].score) }))] })), _jsxs("div", __assign({ className: "r-bot-area" }, { children: [_jsxs("div", __assign({ className: "area-text" }, { children: [_jsx("div", { className: "action-status", style: {
                                            backgroundColor: decideColor(dataset[3].score),
                                            border: "1px solid ".concat(decideBorderColor(dataset[3].score)),
                                        } }), _jsx("div", __assign({ style: {
                                            fontFamily: "var(--font-stack-full-medium)",
                                        } }, { children: dataset[3].label })), dataset[3].tooltip && (_jsx(CardTooltip, { tooltip: dataset[3].tooltip, questionIcon: _jsx(Icon, { path: IconPathName.mdiFilledQuestion, style: { color: "var(--grey-color-70)" } }) }))] })), _jsxs("div", __assign({ className: "area-percentage" }, { children: [_jsx("div", { children: "".concat(Math.round(dataset[3].score), "%") }), dataset[3].onClick && (_jsx(ArrowForwardOutlinedIcon, { className: "action-arrow", onClick: dataset[3].onClick }))] })), _jsx("div", __assign({ className: "pill-score", style: {
                                    backgroundColor: decideColor(dataset[3].score),
                                    fontFamily: "var(--font-stack-full-medium)",
                                } }, { children: decideText(dataset[3].score) }))] }))] }))] })));
};
export default HeidrickScoreChart;
